import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { governedSlugOf, type ModuleCatalogRecord } from "../../modules/modules.types";
import type { DevOpsEventRecord } from "../../clients/clients.types";
import {
  DEVOPS_LINK_WIZARD_EVENT_TYPES,
  type DevOpsAttention,
  type DevOpsOverview,
  type DevOpsOverviewActivity,
  type DevOpsOverviewClientRef,
  type DevOpsOverviewLink,
  type DevOpsOverviewModuleRef,
  type DevOpsOverviewProject,
  type DevOpsOverviewSubscriptions,
  type DevOpsSubscriptionCreatePayload,
} from "../devops.types";
import type DevOpsScopeStore from "../shared/DevOpsScopeStore";
import {
  DEVOPS_LINK_DIALOG_NAME,
  type DevOpsLinkDialogOpenOptions,
} from "../shared/DevOpsLinkDialogStore";
import {
  findOverviewProject,
  loadedOverviews,
  projectKey,
  type ProjectClientsView,
} from "../shared/projectClients";
import { projectLabel } from "../DevOpsOverview/devopsOverview.helpers";
import { isSessionEnded } from "stores/sessionGuard";
import { swallowedFailure, swallowedMessage } from "shared/loadOrError";

/**
 * The Project 360 tabs. A tab's `name` is both its label and its deep-link
 * token (`<Tabs useRouterState>` writes it into `?tabName=`), so these are
 * the strings the route's tab sync keys on — change them here only.
 */
export const DEVOPS_PROJECT_TABS = {
  overview: "Overview",
  pipelines: "Pipelines",
  releases: "Releases",
  repos: "Repositories",
  workItems: "Work Items",
} as const;

export type DevOpsProjectTab = (typeof DEVOPS_PROJECT_TABS)[keyof typeof DEVOPS_PROJECT_TABS];

/**
 * The one-click subscription targets on the Overview tab — the same three
 * the Client 360 link wizard offers, because they are the ones the inbound
 * receiver parses into the per-client live surfaces.
 */
export const PROJECT_QUICK_EVENT_TYPES = DEVOPS_LINK_WIZARD_EVENT_TYPES;

/** What the link Edit dialog may change (the API's patchable pair). */
export interface DevOpsLinkPatch {
  clientId?: string | null;
  governedSlug?: string | null;
}

export interface DevOpsLinkClientOption {
  id: string;
  name: string;
  code: string | null;
}

export interface DevOpsLinkModuleOption {
  slug: string;
  displayName: string;
}

/**
 * The board stores a tab embeds, by the ONE surface Project 360 drives them
 * through. `dispose` is optional: only the boards with a live feed have one.
 */
interface BoardLike {
  initialize(): Promise<void>;
  reload(): Promise<void>;
  dispose?(): void;
}

/** rootStore key of the board behind each board tab. */
const BOARD_STORE_BY_TAB: Record<string, string> = {
  [DEVOPS_PROJECT_TABS.pipelines]: "pipelinesBoardStore",
  [DEVOPS_PROJECT_TABS.releases]: "releasesStore",
  [DEVOPS_PROJECT_TABS.repos]: "devopsReposViewStore",
  [DEVOPS_PROJECT_TABS.workItems]: "workItemsStore",
};

/** How many recent events the Overview tab lists. */
export const RECENT_EVENT_LIMIT = 50;

/**
 * The RAW Feathers service surface (`rootStore.app.service(path)`).
 *
 * `ServiceStore.create/patch/remove` swallow a rejection (they record
 * `errorOnCreate` etc. and resolve null), so a refused write would read as a
 * success. Every write here changes attribution a human must be told about
 * when it fails ("duplicate link", "not an operator"), so they go through
 * the raw service when the app exposes one — the same pattern as
 * `ReadAiSettingsStore.rawService` — and fall back to the wrapper, reading
 * its recorded error instead of trusting a null result.
 */
interface RawService {
  create(data: object): Promise<unknown>;
  patch(id: string, data: object): Promise<unknown>;
  remove(id: string): Promise<unknown>;
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}


/**
 * The `devops-overview` find() resolves the composite itself; a wrapper may
 * hand it back inside a `{ data: [composite] }` envelope. Tolerate both and
 * never mistake an empty envelope for an overview.
 */
function pickOverview(result: unknown): DevOpsOverview | null {
  const isOverview = (v: unknown): v is DevOpsOverview =>
    !!v && typeof v === "object" && Array.isArray((v as { projects?: unknown }).projects);
  if (isOverview(result)) return result;
  const data = (result as { data?: unknown } | null | undefined)?.data;
  if (Array.isArray(data) && isOverview(data[0])) return data[0];
  return null;
}

/** Read a devops event's summary, JSON string or object, as a record. */
function summaryOf(event: DevOpsEventRecord): Record<string, unknown> | null {
  const raw = event.summary;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Does an event belong to a project? By the row's own project columns when
 * it has them (Migration A, 2026-09-03): the id, or — when the webhook named
 * the project by name only and the id column is null — the name. A row from
 * before those columns is judged by what its `summary` JSON carried (the
 * `project` name / `projectId`). The connection must agree when the row has
 * one. {@link DevOpsProjectStore.recentEvents} filters the cache with this.
 */
function eventMatchesProject(
  event: DevOpsEventRecord,
  connectionId: string,
  projectId: string,
  projectName: string | null
): boolean {
  if (event.connectionId && event.connectionId !== connectionId) return false;
  const columnId = event.devopsProjectId;
  if (typeof columnId === "string" && columnId) {
    return columnId.toLowerCase() === projectId.toLowerCase();
  }
  const columnName = event.devopsProjectName;
  if (typeof columnName === "string" && columnName) {
    return !!projectName && columnName === projectName;
  }
  const summary = summaryOf(event);
  if (!summary) return false;
  const sId = summary.projectId;
  if (typeof sId === "string" && sId.toLowerCase() === projectId.toLowerCase()) return true;
  const sName = summary.project;
  return !!projectName && typeof sName === "string" && sName === projectName;
}

/** An event's time as a number for newest-first ordering (unparseable → oldest). */
function eventStamp(event: DevOpsEventRecord): number {
  const t = Date.parse(event.occurredAt ?? event.createdAt ?? "");
  return Number.isFinite(t) ? t : 0;
}

/**
 * Project 360 — everything HQ knows about ONE Azure DevOps project, on one
 * page: the clients it is linked to, the modules attributed to it (and
 * whether approval routing actually creates work items there), its links,
 * its service-hook subscriptions, its recent activity, and the boards
 * (Pipelines / Releases / Repositories / Work Items) as tabs.
 *
 * Plain class with `makeAutoObservable` (app store `devopsProjectStore`).
 *
 * IDENTITY is the route's `:connectionId/:projectId`; the overview's project
 * `key` is `${connectionId}:${projectId}`. The page reads ONE thing for its
 * facts — a `devops-overview` find scoped to the connection — and holds that
 * RESPONSE (`overview`) rather than the entity cache: it is a composite, not
 * a row, and the boards' Client strip reads it too (see
 * shared/projectClients.ts).
 *
 * THE SHARED SCOPE: on enter this store sets `devopsScopeStore` to this
 * project, so the board bodies embedded in the tabs — which read that scope —
 * show this project without anyone choosing it again. A board tab is primed
 * (its store's `initialize()`) the first time it is opened for this project,
 * from the route's params-change hook, never from a component effect.
 *
 * WRITES (subscribe / remove link / patch link) go through the raw service
 * (see {@link RawService}) and re-find the overview afterwards, so the page
 * shows what the server now holds, not what the click hoped for.
 *
 * EVENTS: `devops-events` rows are a persisted entity, so they live in that
 * ServiceStore's cache, not here — `recentEvents` is a COMPUTED over it
 * (this connection + project, newest first, capped) and `loadEvents` only
 * fills the cache. It asks TWICE: by `devopsProjectId`, and — when a name is
 * known — by `devopsProjectName`, because the columns Migration A added are
 * populated independently and an event webhooked before the project was
 * linked carries the name with a permanently NULL id. Against an API that
 * predates the id column, that first query is refused (or swallowed to
 * null), and the loader falls back to the newest events overall, which the
 * computed narrows by the summary's project name; `eventsFallback` says so,
 * because a name match is weaker evidence than a column.
 */
class DevOpsProjectStore {
  rootStore: RootStore;

  connectionId: string | null = null;
  projectId: string | null = null;

  /** The connection-scoped `devops-overview` response this page is drawn from. */
  overview: DevOpsOverview | null = null;
  loading = false;
  error: string | null = null;

  eventsLoading = false;
  eventsError: string | null = null;
  /** True when the events came through the name-filtered fallback (see class doc). */
  eventsFallback = false;

  /** The write in flight (`subscribe:<eventType>` / `remove:<id>` / `patch:<id>`), or null. */
  writing: string | null = null;
  writeError: string | null = null;

  /** The link the Edit dialog is open on, and its form. */
  editingLinkId: string | null = null;
  linkForm: DevOpsLinkPatch = { clientId: null, governedSlug: null };

  /** `${key}|${tab}` stamps of the board tabs primed for the open project. */
  private primedTabs = new Set<string>();
  private loadSeq = 0;

  /** Same idea as {@link loadSeq}, for the events read. */
  private eventsSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ---- Service accessors ----------------------------------------------------

  /**
   * A registered ServiceStore by service path. The manager is the primary
   * path; the conventional `rootStore.<name>Store` key is the fallback ONLY
   * when it quacks like a ServiceStore — `devopsOverviewStore` is the
   * Overview PAGE's view store, not the `devops-overview` service.
   */
  private service(path: string, fallbackKey: string): ServiceStoreType | null {
    const managed = this.rootStore.serviceStoreManager?.getStore(path) as unknown as
      | ServiceStoreType
      | null
      | undefined;
    if (managed) return managed;
    const direct = this.rootStore[fallbackKey] as { find?: unknown } | undefined;
    return direct && typeof direct.find === "function"
      ? (direct as unknown as ServiceStoreType)
      : null;
  }

  private get overviewService(): ServiceStoreType | null {
    return this.service("devops-overview", "devopsOverviewStore");
  }

  private get eventsService(): ServiceStoreType | null {
    return this.service("devops-events", "devopsEventsStore");
  }

  private get linksService(): ServiceStoreType | null {
    return this.service("devops-links", "devopsLinksStore");
  }

  private get subscriptionsService(): ServiceStoreType | null {
    return this.service("devops-subscriptions", "devopsSubscriptionsStore");
  }

  private get moduleCatalogService(): ServiceStoreType | null {
    return this.service("module-catalog", "moduleCatalogStore");
  }

  /** The raw Feathers service for a path (see {@link RawService}). */
  private rawService(path: string): RawService | null {
    const app = this.rootStore.app as unknown as
      | { service?: (p: string) => RawService }
      | undefined;
    return app?.service?.(path) ?? null;
  }

  private get scopeStore(): DevOpsScopeStore | null {
    return (this.rootStore.devopsScopeStore as DevOpsScopeStore | undefined) ?? null;
  }

  /** The global tenant list (name resolution + the Edit dialog's client picker). */
  private get globalClientStore():
    | { clients?: Array<{ id: string; name: string; code?: string | null }>; fetchClients?: () => Promise<void> }
    | undefined {
    return this.rootStore.clientStore as
      | { clients?: Array<{ id: string; name: string; code?: string | null }>; fetchClients?: () => Promise<void> }
      | undefined;
  }

  private boardFor(tabName: string | undefined): BoardLike | null {
    if (!tabName) return null;
    const key = BOARD_STORE_BY_TAB[tabName];
    if (!key) return null;
    const store = this.rootStore[key] as Partial<BoardLike> | undefined;
    return store && typeof store.initialize === "function" && typeof store.reload === "function"
      ? (store as BoardLike)
      : null;
  }

  // ---- Identity -------------------------------------------------------------

  /** The overview's project key for the open identity, or null. */
  get key(): string | null {
    return this.connectionId && this.projectId
      ? projectKey(this.connectionId, this.projectId)
      : null;
  }

  /**
   * Whether the current user may link / subscribe / edit here (operator+).
   * The API is the real enforcement point; this only governs the buttons.
   */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  /** The overview's row for this project, from THIS page's response. */
  get project(): DevOpsOverviewProject | null {
    if (!this.overview || !this.connectionId || !this.projectId) return null;
    return findOverviewProject(this.overview, this.connectionId, this.projectId);
  }

  /** Loaded, and the overview does not list this project: HQ knows nothing of it. */
  get notFound(): boolean {
    return this.overview !== null && this.project === null;
  }

  /**
   * A REAL name for the project — the overview's, else the scope's last-seen
   * — or null. What gets written onto links and subscriptions and matched
   * against event summaries; never a placeholder.
   */
  get knownProjectName(): string | null {
    return this.project?.name ?? this.scopeStore?.projectName ?? null;
  }

  /**
   * Display name: a real name when one is known, else what HQ knows the
   * project by (its id — an id-only project has none; see `projectLabel`),
   * else null until anything is known.
   */
  get projectName(): string | null {
    return this.knownProjectName ?? (this.project ? projectLabel(this.project) : null);
  }

  get clients(): DevOpsOverviewClientRef[] {
    return this.project?.clients ?? [];
  }

  get modules(): DevOpsOverviewModuleRef[] {
    return this.project?.modules ?? [];
  }

  get links(): DevOpsOverviewLink[] {
    return this.project?.links ?? [];
  }

  get subscriptions(): DevOpsOverviewSubscriptions | null {
    return this.project?.subscriptions ?? null;
  }

  get activity(): DevOpsOverviewActivity | null {
    return this.project?.activity ?? null;
  }

  get attention(): DevOpsAttention[] {
    return this.project?.attention ?? [];
  }

  /** The window the activity rollup covers (the API's default when unknown). */
  get windowDays(): number {
    return this.overview?.windowDays ?? 7;
  }

  /**
   * The API's caveat about the live project list this page asked for
   * (`includeLive: true`), rendered verbatim by the not-found state. Null when
   * the API sent none. It says only that there IS something to report — which
   * of the two very different things it reports is {@link livePartial} vs
   * {@link liveFetched}.
   */
  get liveNote(): string | null {
    return this.overview?.live?.note ?? null;
  }

  /**
   * Azure answered for every connection asked. FALSE alongside a note is a
   * failed read (no PAT, a 403, an outage): this page is then drawn from local
   * records only, and "HQ knows nothing about this project" is a fact about
   * the failure, not about the project.
   */
  get liveFetched(): boolean {
    return this.overview?.live?.fetched === true;
  }

  /**
   * The live list is KNOWN to be truncated (Azure's page cap hit with more to
   * come) — the only state in which "this project may simply not have been in
   * the list" is true. A failed read carries a note but is `partial: false`,
   * so this must never be inferred from {@link liveNote}.
   */
  get livePartial(): boolean {
    return this.overview?.live?.partial === true;
  }

  /**
   * The newest events for this project — computed over the `devops-events`
   * ServiceStore's entity cache (see the class doc; {@link loadEvents} fills
   * it). Kept: rows on this connection that the project columns (or, for
   * older rows, the summary) attribute to this project; newest first by
   * `occurredAt`; capped at {@link RECENT_EVENT_LIMIT}. Empty with no store
   * registered (a bare test rootStore) — never a crash.
   */
  get recentEvents(): DevOpsEventRecord[] {
    const { connectionId, projectId } = this;
    if (!connectionId || !projectId) return [];
    const rows = (this.eventsService?.entities ?? []) as unknown as DevOpsEventRecord[];
    if (rows.length === 0) return [];
    const name = this.knownProjectName;
    return rows
      .filter((e) => eventMatchesProject(e, connectionId, projectId, name))
      .sort((a, b) => eventStamp(b) - eventStamp(a))
      .slice(0, RECENT_EVENT_LIMIT);
  }

  /** The Client strip's view of this project — the header and boards share the component. */
  get projectClients(): ProjectClientsView {
    const project = this.project;
    return {
      known: this.overview !== null,
      project,
      clients: project?.clients ?? [],
    };
  }

  /**
   * Why the routing and the links disagree, in words, or null when they do
   * not. Both facts come from the overview; nothing here is reconciled by
   * guessing — the sentence names the disagreement and stops.
   */
  get mismatchNote(): string | null {
    const project = this.project;
    if (!project) return null;
    const notes: string[] = [];
    if (project.attention.includes("module-mapping-mismatch")) {
      notes.push(
        "Work-item routing (module_catalog.adoProject) and this project's links disagree: a module routes its accepted action items here without a link, or is linked here without routing here."
      );
    }
    if (this.overview?.mismatches?.linkedProjectsNotRoutedByAnyModule?.includes(project.key)) {
      notes.push(
        "This project is linked, but no module's work-item routing names it — accepting an action item will not create a work item here."
      );
    }
    return notes.length > 0 ? notes.join(" ") : null;
  }

  /**
   * Active subscriptions on the connection with no recorded project scope
   * (every row from before per-project scope was recorded) — the API's
   * `connectionWide` count. Their scope is UNKNOWN: they may cover this
   * project, or some other one. Counted by the API, never attributed.
   */
  get hasConnectionWideSubscriptions(): boolean {
    return (this.subscriptions?.connectionWide ?? 0) > 0;
  }

  /** Every quick-subscribe target has a PROJECT-scoped subscription. */
  get quickSubscriptionsComplete(): boolean {
    const have = new Set(this.subscriptions?.eventTypes ?? []);
    return PROJECT_QUICK_EVENT_TYPES.every((t) => have.has(t.value));
  }

  /**
   * The quick-subscribe targets to OFFER: the wizard's three minus what is
   * subscribed project-scoped — and NONE while subscriptions of unknown
   * scope exist. Those rows may already deliver these very events for this
   * project (the API deliberately does not flag such a project
   * `no-subscriptions`), so a one-click subscribe here would register a
   * second Azure hook that double-counts every build. They are re-scoped
   * under DevOps → Subscriptions first; the tab says so.
   */
  get missingQuickSubscriptions(): ReadonlyArray<{ value: string; label: string }> {
    // Nothing is "not subscribed" until the overview says so: before it lands
    // `subscriptions` is null, and offering all three would tell the operator
    // a project has no webhooks when HQ has not yet looked.
    if (!this.overview || !this.subscriptions) return [];
    if (this.hasConnectionWideSubscriptions) return [];
    const have = new Set(this.subscriptions?.eventTypes ?? []);
    return PROJECT_QUICK_EVENT_TYPES.filter((t) => !have.has(t.value));
  }

  // ---- Edit-dialog pickers --------------------------------------------------

  /** Every tenant, for the Edit dialog's client picker. */
  get clientOptions(): DevOpsLinkClientOption[] {
    return (this.globalClientStore?.clients ?? [])
      .map((c) => ({ id: c.id, name: c.name, code: c.code ?? null }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }

  /** Assignable catalog entries, for the Edit dialog's module picker. */
  get moduleOptions(): DevOpsLinkModuleOption[] {
    const rows = (this.moduleCatalogService?.entities ?? []) as unknown as ModuleCatalogRecord[];
    return rows
      .filter((r) => r.assignable !== false)
      .map((r) => ({ slug: governedSlugOf(r), displayName: r.displayName || r.moduleKey }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base" }));
  }

  get editingLink(): DevOpsOverviewLink | null {
    if (!this.editingLinkId) return null;
    return this.links.find((l) => l.id === this.editingLinkId) ?? null;
  }

  /** What the Edit dialog would send — only the fields that changed. */
  get linkFormPatch(): DevOpsLinkPatch {
    const link = this.editingLink;
    if (!link) return {};
    const patch: DevOpsLinkPatch = {};
    if ((this.linkForm.clientId ?? null) !== (link.clientId ?? null)) {
      patch.clientId = this.linkForm.clientId ?? null;
    }
    if ((this.linkForm.governedSlug ?? null) !== (link.governedSlug ?? null)) {
      patch.governedSlug = this.linkForm.governedSlug ?? null;
    }
    return patch;
  }

  get isLinkFormDirty(): boolean {
    return Object.keys(this.linkFormPatch).length > 0;
  }

  // ---- Lifecycle ------------------------------------------------------------

  /**
   * Point the page at a project (route onEnter / onParamsChange) and set the
   * shared scope so the board tabs are already looking at it. A different
   * identity resets the page; the same one (a tab switch re-fires the hook)
   * keeps everything, including the primed board tabs.
   */
  setTarget(connectionId: string, projectId: string) {
    const changed = connectionId !== this.connectionId || projectId !== this.projectId;
    this.connectionId = connectionId;
    this.projectId = projectId;
    if (changed) {
      // Invalidate BOTH reads in flight for the project we are leaving. Their
      // guards compare against these counters, so without the bump an answer
      // for the old project still matches and writes itself onto the new one —
      // its overview, or its events error and fallback flag — and the spinner
      // it left behind never clears because its `finally` is skipped too.
      this.loadSeq += 1;
      this.eventsSeq += 1;
      this.loading = false;
      this.eventsLoading = false;
      this.overview = null;
      this.error = null;
      this.eventsError = null;
      this.eventsFallback = false;
      this.writeError = null;
      this.writing = null;
      this.editingLinkId = null;
      this.primedTabs.clear();
    }
    this.assertScope();
  }

  /**
   * Write this project into the shared scope. The name comes from any loaded
   * overview that lists the project, else the scope's own name when it is
   * already on this project (a board set it), else null until load() knows.
   */
  private assertScope() {
    const scope = this.scopeStore;
    const { connectionId, projectId } = this;
    if (!scope || !connectionId || !projectId) return;
    const known = loadedOverviews(this.rootStore)
      .map((o) => findOverviewProject(o, connectionId, projectId))
      .find((p) => p !== null);
    const keepName =
      scope.connectionId === connectionId && scope.projectId === projectId
        ? scope.projectName
        : null;
    scope.setScope({ connectionId, projectId, projectName: known?.name ?? keepName });
  }

  /**
   * Read the connection-scoped overview and pick this project by key.
   * `includeLive` so a project that exists in Azure but has no link, event or
   * catalog row yet still renders (as `source: 'live'` with its attention),
   * instead of a "not found" for something the operator can see in Azure.
   * A wrapper that swallowed the rejection resolves null — that is an error
   * here, never an empty page.
   */
  async load() {
    const { connectionId, projectId } = this;
    if (!connectionId || !projectId) return;
    const seq = ++this.loadSeq;
    this.loading = true;
    this.error = null;
    try {
      const result = await this.overviewService?.find({
        query: { connectionId, includeLive: true },
      });
      const overview = pickOverview(result);
      if (!overview) {
        throw swallowedFailure(this.overviewService, "errorOnFind", "The server did not return the DevOps overview");
      }
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        this.overview = overview;
        const name = this.project?.name;
        if (name) this.scopeStore?.setScope({ projectName: name });
      });
    } catch (err: unknown) {
      if (seq === this.loadSeq) {
        runInAction(() => {
          if (!isSessionEnded(err)) this.error = messageOf(err, "Failed to load the project");
        });
      }
      console.error("Failed to load DevOps project overview:", err);
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
    if (seq === this.loadSeq) await this.loadEvents();
  }

  /**
   * Fill the `devops-events` cache with this project's newest events;
   * {@link recentEvents} is computed over it.
   *
   * TWO reads against a current API, because the project columns are two
   * columns and a row may carry either:
   *  1. by the `devopsProjectId` column (Migration A, 2026-09-03), newest
   *     first, capped at {@link RECENT_EVENT_LIMIT};
   *  2. by the `devopsProjectName` column, same cap — ONLY when a name is
   *     known. The persist path leaves `devopsProjectId` NULL when neither
   *     the hint nor a `devops_links` row could resolve the name to a GUID
   *     (see the API's `devops-events.schema.ts`), and the backfill never
   *     guessed one, so an event webhooked BEFORE the project was linked is
   *     name-only, permanently. Read (1) cannot see those rows — its filter
   *     is the column they have no value in — so without read (2) the
   *     name-matching branch of {@link eventMatchesProject} could only ever
   *     fire on rows nothing had fetched. `devops-events` admits
   *     `devopsProjectName` as a query property precisely for this.
   *
   * THE BOUND is `2 × RECENT_EVENT_LIMIT` rows added to the shared cache;
   * `recentEvents` shows the newest {@link RECENT_EVENT_LIMIT} of the union,
   * and drops any name-read row whose id column names a DIFFERENT project.
   * Read (2) is SUPPLEMENTARY: an API that predates the column refuses it (or
   * swallows it to null) and the page keeps read (1)'s rows rather than
   * reporting a failure it does not have.
   *
   * FALLBACK, for an API that predates BOTH columns and refuses (or swallows)
   * read (1): the newest events overall — the computed then keeps only the
   * rows whose summary names this project, weaker evidence, and
   * `eventsFallback` says so on the page. A swallowed fallback is an error.
   */
  async loadEvents() {
    const { connectionId, projectId } = this;
    if (!connectionId || !projectId) return;
    // Same guard `load()` uses: a second project opened while this read is in
    // flight must not have its flags written by the first project's answer.
    const seq = ++this.eventsSeq;
    this.eventsLoading = true;
    this.eventsError = null;
    const service = this.eventsService;
    const projectName = this.knownProjectName;
    try {
      let primary: unknown = null;
      try {
        primary = await service?.find({
          query: {
            devopsProjectId: projectId,
            $limit: RECENT_EVENT_LIMIT,
            $sort: { occurredAt: -1 },
          },
        });
      } catch (err) {
        console.error("devops-events by devopsProjectId refused; falling back to a name match:", err);
      }
      let fallback = false;
      if (primary == null) {
        // Another project was opened while the column query was in flight —
        // do not spend a second read on a page nobody is looking at.
        if (seq !== this.eventsSeq) return;
        const result = await service?.find({
          query: { $limit: 200, $sort: { occurredAt: -1 } },
        });
        if (result == null) {
          throw swallowedFailure(service, "errorOnFind", "The server did not return the DevOps events");
        }
        fallback = true;
      } else if (projectName && seq === this.eventsSeq) {
        // Read (2): the name-only rows. Supplementary — a refusal or a
        // swallowed null here leaves read (1)'s rows standing and says
        // nothing, because the page HAS its events.
        try {
          const byName = await service?.find({
            query: {
              devopsProjectName: projectName,
              $limit: RECENT_EVENT_LIMIT,
              $sort: { occurredAt: -1 },
            },
          });
          if (byName == null) {
            console.error(
              "devops-events by devopsProjectName returned nothing:",
              swallowedMessage(service, "errorOnFind") ?? "no reason recorded"
            );
          }
        } catch (err) {
          console.error("devops-events by devopsProjectName refused:", err);
        }
      }
      if (seq !== this.eventsSeq) return;
      runInAction(() => {
        this.eventsFallback = fallback;
      });
    } catch (err: unknown) {
      if (seq !== this.eventsSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.eventsError = messageOf(err, "Failed to load recent activity");
      });
      console.error("Failed to load DevOps project events:", err);
    } finally {
      if (seq === this.eventsSeq) {
        runInAction(() => {
          this.eventsLoading = false;
        });
      }
    }
  }

  /**
   * Prime the board behind a tab the first time it opens for this project —
   * the board's own `initialize()`, which adopts the shared scope. Called from
   * the route's enter / params-change hooks (a tab switch rewrites the query
   * string and fires the latter). `refresh` re-runs a primed board's `reload()`
   * — the header's refresh button on a board tab.
   */
  async syncTab(tabName: string | undefined, options: { refresh?: boolean } = {}) {
    const key = this.key;
    const board = this.boardFor(tabName);
    if (!key || !board) return;
    const stamp = `${key}|${tabName}`;
    const primed = this.primedTabs.has(stamp);
    if (primed && !options.refresh) return;
    // Re-assert the scope right before the board reads it, in case a board
    // cleared the project (adoptScope on a project Azure no longer lists).
    this.assertScope();
    this.primedTabs.add(stamp);
    if (primed) {
      await board.reload();
    } else {
      await board.initialize();
    }
  }

  /** The header's refresh: the overview + events, and the open board tab. */
  async refresh(tabName?: string) {
    await Promise.all([this.load(), this.syncTab(tabName, { refresh: true })]);
  }

  /**
   * Route onExit: tear down the live feeds of the boards this page primed
   * (the boards' own routes do this on their exit; here they never ran).
   */
  dispose() {
    for (const tab of Object.keys(BOARD_STORE_BY_TAB)) {
      this.boardFor(tab)?.dispose?.();
    }
    this.primedTabs.clear();
    this.editingLinkId = null;
  }

  // ---- Writes ---------------------------------------------------------------

  /** Run one write, then re-find so the page shows what the server holds. */
  private async write(label: string, fn: () => Promise<void>): Promise<boolean> {
    if (this.writing) return false;
    runInAction(() => {
      this.writing = label;
      this.writeError = null;
    });
    try {
      await fn();
      await this.load();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.writeError = messageOf(err, "The change was not saved");
      });
      console.error(`DevOps project write failed (${label}):`, err);
      return false;
    } finally {
      runInAction(() => {
        this.writing = null;
      });
    }
  }

  isWriting(label: string): boolean {
    return this.writing === label;
  }

  /** Dismiss the last write's error (the alert's close button). */
  clearWriteError() {
    this.writeError = null;
  }

  /**
   * Register a service-hook subscription scoped to THIS project — the same
   * payload the Client 360 link wizard sends (`publisherInputs.projectId`;
   * the API forwards `publisherInputs` verbatim to Azure), plus the project's
   * name when one is known, so the row is not a GUID with no name (which the
   * overview would list as `name: null`).
   */
  async subscribe(eventType: string): Promise<boolean> {
    const { connectionId, projectId } = this;
    if (!connectionId || !projectId) return false;
    return this.write(`subscribe:${eventType}`, async () => {
      const payload: DevOpsSubscriptionCreatePayload = {
        connectionId,
        eventType,
        publisherInputs: { projectId },
      };
      const projectName = this.knownProjectName;
      if (projectName) payload.projectName = projectName;
      const raw = this.rawService("devops-subscriptions");
      if (raw) {
        await raw.create(payload);
        return;
      }
      const result = await this.subscriptionsService?.create(payload as never);
      if (result == null) {
        throw swallowedFailure(this.subscriptionsService, "errorOnCreate", "The server did not confirm the subscription");
      }
    });
  }

  /** Remove a link (soft-delete server-side). Operator+; the API re-checks. */
  async removeLink(id: string): Promise<boolean> {
    return this.write(`remove:${id}`, async () => {
      const raw = this.rawService("devops-links");
      if (raw) {
        await raw.remove(id);
        return;
      }
      const result = await this.linksService?.remove(id);
      if (result == null) {
        throw swallowedFailure(this.linksService, "errorOnRemove", "The server did not confirm the removal");
      }
    });
  }

  /** Re-attribute a link: its client and/or its governed module. */
  async patchLink(id: string, patch: DevOpsLinkPatch): Promise<boolean> {
    const body: Record<string, string | null> = {};
    if (patch.clientId !== undefined) body.clientId = patch.clientId;
    if (patch.governedSlug !== undefined) body.governedSlug = patch.governedSlug;
    if (Object.keys(body).length === 0) return true;
    return this.write(`patch:${id}`, async () => {
      const raw = this.rawService("devops-links");
      if (raw) {
        await raw.patch(id, body);
        return;
      }
      const result = await this.linksService?.patch(id, body as never);
      if (result == null) {
        throw swallowedFailure(this.linksService, "errorOnPatch", "The server did not confirm the change");
      }
    });
  }

  // ---- Edit-link dialog -----------------------------------------------------

  /** Open the Edit dialog on a link, seeded from the row; loads the pickers. */
  startEditLink(linkId: string) {
    const link = this.links.find((l) => l.id === linkId);
    if (!link) return;
    this.editingLinkId = linkId;
    this.linkForm = {
      clientId: link.clientId ?? null,
      governedSlug: link.governedSlug ?? null,
    };
    this.writeError = null;
    void this.ensurePickerData();
  }

  setLinkForm(patch: DevOpsLinkPatch) {
    this.linkForm = { ...this.linkForm, ...patch };
  }

  cancelEditLink() {
    this.editingLinkId = null;
  }

  /** Save the Edit dialog — only the changed fields are sent. */
  async saveLinkEdit(): Promise<boolean> {
    const id = this.editingLinkId;
    if (!id) return false;
    const patch = this.linkFormPatch;
    if (Object.keys(patch).length === 0) {
      this.editingLinkId = null;
      return true;
    }
    const ok = await this.patchLink(id, patch);
    if (ok) {
      runInAction(() => {
        this.editingLinkId = null;
      });
    }
    return ok;
  }

  /** The pickers' vocabularies, loaded once, best-effort and logged. */
  private async ensurePickerData() {
    const clients = this.globalClientStore;
    const tasks: Promise<unknown>[] = [];
    if (clients?.fetchClients && !(clients.clients && clients.clients.length)) {
      tasks.push(
        clients.fetchClients().catch((err) => console.error("Failed to load clients:", err))
      );
    }
    const catalog = this.moduleCatalogService;
    if (catalog && (catalog.entities ?? []).length === 0) {
      tasks.push(
        Promise.resolve(
          catalog.find({ query: { $limit: 500, $sort: { displayName: 1 } } })
        ).catch((err) => console.error("Failed to load the module catalog:", err))
      );
    }
    await Promise.all(tasks);
  }

  // ---- Link dialog ----------------------------------------------------------

  /**
   * Open the shared "Link a project" dialog pre-filled with THIS project.
   * The dialog's store owns the form and opens the routed dialog itself
   * (`devopsLinkDialogStore.open`); `onSaved` re-reads this page. Without
   * that store registered, the routed dialog is still opened by name, with
   * the pre-fill on the query string (the router types `dialogContext` as a
   * string), so the page never has a dead button.
   */
  openLinkDialog() {
    const { connectionId, projectId } = this;
    if (!connectionId || !projectId) return;
    // A REAL name only: the dialog denormalises it onto the link, and fills
    // a missing one in from the live list — an id placeholder would block that.
    const preselect = {
      connectionId,
      projectId,
      projectName: this.knownProjectName ?? undefined,
    };
    const dialogStore = this.rootStore.devopsLinkDialogStore as
      | { open?: (options: DevOpsLinkDialogOpenOptions) => void }
      | undefined;
    if (dialogStore && typeof dialogStore.open === "function") {
      dialogStore.open({ preselect, onSaved: () => void this.load() });
      return;
    }
    this.rootStore.routerStore?.openDialog?.({
      dialogName: DEVOPS_LINK_DIALOG_NAME,
      dialogContext: JSON.stringify(preselect),
    });
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.connectionId = null;
    this.projectId = null;
    this.overview = null;
    this.loading = false;
    this.error = null;
    this.eventsLoading = false;
    this.eventsError = null;
    this.eventsFallback = false;
    this.writing = null;
    this.writeError = null;
    this.editingLinkId = null;
    this.linkForm = { clientId: null, governedSlug: null };
    this.primedTabs = new Set<string>();
    this.loadSeq += 1;
    this.eventsSeq += 1;
  }
}

export default DevOpsProjectStore;
